<template>
  <div class="content">
    <!-- 操作 -->
    <div class="operate-group">
      <div class="operate-group-left">
        <el-button
          :disabled="checkedRows.length !== 1 || checkedRows[0].status === 'handled'"
          @click.native="delFun"
          type="primary">
          <svg-icon icon-class="deal"/>
          {{ t('alarm.deal') }}
        </el-button>
      </div>
      <div class="operate-group-right">
        <search-toolbar
          v-model:quick-field="quickSearchField"
          v-model:quick-value="quickSearchValue"
          :quick-fields="quickSearchFields"
          :default-quick-field="defaultQuickField"
          :search-mode="searchMode"
          :filter-summary="filterStr"
          :advanced-button-text="advancedFilterButtonText"
          :choose-placeholder="t('placeholder.choose')"
          :search-placeholder="t('placeholder.search')"
          :refresh-text="t('table.refresh')"
          @quick-field-change="handleQuickFieldChange"
          @quick-value-change="handleQuickValueChange"
          @quick-input="handleQuickInput"
          @open-advanced="openAdvancedDrawer"
          @reset-search="resetAllSearch"
          @refresh="getList" />
      </div>
    </div>
    <!-- 表格 -->
    <custom-table
      v-model:checkedRows="checkedRows"
      :isShowCheck="true"
      :tableData="tableData"
      :height="$store.getters.windowHeight - 300 < 200 ? 200 : $store.getters.windowHeight - 300"
      :cellClassName="cellClassName"
      :cellClick="cellClick"
      @getTableData="getList"
    />
    <!-- 详情 -->
    <el-drawer
      v-model="visibleDetail"
      :close-on-click-modal="true"
      :destroy-on-close="true"
      :title="t('route.alarmMessageDetail')"
      custom-class="host-detail-drawer"
      @close="getList"
      append-to-body
      size="60%">
      <Detail ref="detailRef" />
    </el-drawer>
    <advanced-filter-drawer
      v-model:visible="advancedDrawerVisible"
      :title="advancedFilterButtonText"
      width="50%"
      :has-other-condition="false"
      :filter-condition="advancedFilterCondition"
      :other-condition-list="advancedOtherConditionList"
      @submit="handleAdvancedSubmit"
      @cancel="handleAdvancedCancel" />
  </div>
</template>

<script setup>
import { reactive, watch, ref, nextTick, computed } from 'vue'
import AdvancedFilterDrawer from '@/components/AdvancedFilterDrawer'
import SearchToolbar from '@/components/SearchToolbar'
import { getAlarmMessages, updateAlarmMessage } from '@/api/alarmAdmin/alarmMessege'
import { getResourceTypesFun } from '@/composables/alarm'
import getTableList from '@/composables/getList' // 获取列表
import useListSearch from '@/composables/useListSearch'
import { useI18n } from 'vue-i18n'
import store from '@/store'
import { ElMessageBox } from 'element-plus'
import Detail from './detail.vue'
const { t } = useI18n()
const detailRef = ref()
const visibleDetail = ref(false)
// 获取资源类型
const resourceTypeOption = getResourceTypesFun({ scene: 'system' })
// 定义搜索字段
const search = reactive({
  resource_type_id: '',
  type: '',
  level: '',
  status: '',
  read: '',
  resource: ''
})
// 获取列表
const { listData, listTotal, getList, searchFun } = getTableList(search, getAlarmMessages, { data: 'alarm_messages', total: 'total' })
const defaultQuickField = 'resource'
const normalizeOptions = (options = []) => {
  return options.map(item => ({
    label: item.label ?? item.display_name ?? item.name ?? '',
    value: item.value ?? item.id ?? ''
  }))
}
const createAlarmSearchFields = () => {
  return {
    resource: {
      key: 'resource',
      label: t('alarm.alarm_resource'),
      filterType: 'filter',
      type: 'input',
      value: '',
      defaultValue: '',
      trim: true,
      placeholder: t('placeholder.search'),
      clearable: true
    },
    resource_type_id: {
      key: 'resource_type_id',
      label: t('alarm.resource_type'),
      filterType: 'filter',
      type: 'select',
      value: '',
      defaultValue: '',
      options: normalizeOptions([
        { label: t('table.all'), value: '' },
        ...resourceTypeOption.value
      ]),
      placeholder: t('placeholder.choose'),
      clearable: true
    },
    type: {
      key: 'type',
      label: t('alarm.alarm_message_type'),
      filterType: 'filter',
      type: 'select',
      value: '',
      defaultValue: '',
      options: [
        { label: t('table.all'), value: '' },
        { label: t('alarm.event_alarm_message'), value: 'event' },
        { label: t('alarm.resource_alarm_message'), value: 'resource' }
      ],
      placeholder: t('placeholder.choose'),
      clearable: true
    },
    level: {
      key: 'level',
      label: t('alarm.level'),
      filterType: 'filter',
      type: 'select',
      value: '',
      defaultValue: '',
      options: [
        { label: t('table.all'), value: '' },
        { label: t('alarm.normal'), value: 'normal' },
        { label: t('alarm.serious'), value: 'serious' },
        { label: t('alarm.emergent'), value: 'emergent' }
      ],
      placeholder: t('placeholder.choose'),
      clearable: true
    },
    status: {
      key: 'status',
      label: t('table.status'),
      filterType: 'filter',
      type: 'select',
      value: '',
      defaultValue: '',
      options: [
        { label: t('table.all'), value: '' },
        { label: t('alarm.untreated'), value: 'unhandled' },
        { label: t('alarm.processed'), value: 'handled' }
      ],
      placeholder: t('placeholder.choose'),
      clearable: true
    },
    read: {
      key: 'read',
      label: t('alarm.read'),
      filterType: 'filter',
      type: 'select',
      value: '',
      defaultValue: '',
      options: [
        { label: t('table.all'), value: '' },
        { label: t('alarm.reads'), value: 'true' },
        { label: t('alarm.unread'), value: 'false' }
      ],
      placeholder: t('placeholder.choose'),
      clearable: true
    }
  }
}
const quickSearchFields = computed(() => createAlarmSearchFields())
const advancedFilterButtonText = computed(() => t('table.advancedFilter'))
const {
  searchMode,
  quickSearchField,
  quickSearchValue,
  advancedDrawerVisible,
  filterStr,
  advancedFilterCondition,
  resetAllSearch,
  handleQuickFieldChange,
  handleQuickValueChange,
  handleQuickInput,
  openAdvancedDrawer,
  handleAdvancedSubmit,
  handleAdvancedCancel
} = useListSearch({
  search,
  searchFun,
  quickFields: quickSearchFields,
  defaultQuickField,
  createAdvancedFilterCondition: createAlarmSearchFields
})
const advancedOtherConditionList = computed(() => ({}))
const checkedRows = ref([])
watch(
  listData,
  (nv, ov) => {
    const totalPages = Math.ceil(listTotal / store.getters.pageSize)
    if ((store.getters.currentPage > 1) && (totalPages < store.getters.currentPage)) {
      store.dispatch('app/setCurrentPage', totalPages)
      getList()
    }
  },
  {
    deep: true
  }
)
// 处理消息
const delFun = (row) => {
  ElMessageBox.confirm(
    `${checkedRows.value[0].display_name}`,
    t('alarm.dealMessageTip'),
    {
      confirmButtonText: t('form.submit'),
      cancelButtonText: t('form.cancel'),
      dangerouslyUseHTMLString: true,
      type: 'warning'
    }
  )
    .then(async() => {
      const parm = {
        alarm_message_id: checkedRows.value[0].id,
        alarm_message: {
          status: 'handled',
          read: true
        }
      }
      await updateAlarmMessage(parm)
      getList()
    })
}
// table data
const tableData = reactive({
  header: [
    {
      key: 'display_name',
      value: t('alarm.message_name'),
      fixed: 'left',
      minWidth: '190px',
      sortable: true,
      abnormalValue: (row) => {
        return `<i class="${row.read ? '' : 'unread'}"></i>${row.display_name}`
      }
    },
    {
      key: 'resource',
      minWidth: '190px',
      sortable: true,
      value: t('alarm.alarm_resource')
    },
    {
      key: 'display_status',
      minWidth: '140px',
      sortable: true,
      value: t('alarm.alarm_status'),
      isShowCircle: true,
      icon: 'status',
      abnormalValue: (row) => row.display_status
    },
    {
      key: 'display_level',
      minWidth: '140px',
      sortable: true,
      value: t('alarm.level'),
      abnormalValue: (row) => {
        return `<i class="fa fa-warning ${row.level}"></i>${row.display_level}`
      }
    },
    {
      key: 'display_type',
      minWidth: '140px',
      sortable: true,
      value: t('alarm.alarm_message_type')
    },
    {
      key: 'display_resource_type',
      minWidth: '180px',
      sortable: true,
      value: t('alarm.resource_type')
    },
    {
      key: 'occurred_at',
      minWidth: '180px',
      sortable: true,
      value: t('table.occurred_at')
    }
  ],
  data: listData,
  total: listTotal
})
// 单元格class
const cellClassName = (row) => {
  if (row.column.rawColumnKey === 'display_name') {
    return 'isClick'
  }
}
// 详情
// 阅读消息
const read = (parm) => {
  updateAlarmMessage(parm)
}
const cellClick = async(row, column) => {
  if (column.rawColumnKey === 'display_name') {
    if (!row.read) {
      const parm = {
        alarm_message_id: row.id,
        alarm_message: {
          read: true
        }
      }
      read(parm)
    }
    visibleDetail.value = true
    await nextTick()
    detailRef.value.getDetailFun(row.id)
    // router.push({
    //   name: 'alarmMessageDetail',
    //   params: {
    //     name: row.display_name,
    //     id: row.id
    //   }
    // })
  }
}
</script>

<style lang="scss">
@import '@/styles/variables.scss';
.unread {
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--el-color-error);
  margin-right: 4px;
}
</style>

<style lang="scss" scoped>
.operate-group {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: nowrap;
  gap: 16px;
}
.operate-group-left {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}
.operate-group-right {
  margin-top: 0 !important;
  margin-left: auto;
  flex-wrap: nowrap !important;
}
.operate-group-right :deep(.search-toolbar) {
  flex-wrap: nowrap;
}
</style>
